import React from 'react';
import { Feather, Award, Sparkles, Heart } from 'lucide-react';
import { LuxuryModal } from './ui/LuxuryModal';

interface RashmiStoryModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const RashmiStoryModal: React.FC<RashmiStoryModalProps> = ({
  isOpen,
  onClose,
}) => {
  return (
    <LuxuryModal
      isOpen={isOpen}
      onClose={onClose}
      subtitle="Founder's Letter"
      icon={<Feather className="w-3.5 h-3.5" />}
      ariaLabel="Founder's Letter from Rashmi"
      maxWidthClass="max-w-2xl sm:max-w-3xl"
    >
      <div className="space-y-6 sm:space-y-8">
        {/* Main Quote Heading */}
        <h2 className="text-2xl sm:text-4xl md:text-5xl font-serif text-[#3C2D22] font-normal leading-[1.2]">
          "We live in a world that moves too fast. Noor Pieces is my invitation to slow down."
        </h2>

        {/* Narrative Paragraphs */}
        <div className="space-y-4 text-sm sm:text-base text-[#6A5948] font-sans font-normal leading-relaxed">
          <p>
            My name is <strong className="text-[#3C2D22] font-semibold">Rashmi</strong>. I started Noor Pieces in a quiet corner of my sunlit workshop because I believed that light should be more than illumination—it should be a sensory refuge.
          </p>
          <p>
            There are no assembly lines here. No automated factory nozzles, no chemical accelerants, no shortcuts. Every single candle is poured by hand in micro-batches of twelve. We hand-wick each amber vessel with organic cotton, measure pure botanical oils with apothecary scales, and let the wax cure in natural ambient air for two full weeks.
          </p>
          <p>
            When you light a Noor Pieces candle, you are not simply burning wax. You are releasing hours of quiet intention, sustainable Earth ingredients, and a feeling of profound home peace.
          </p>
        </div>

        {/* Artisanal Pledges Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t border-[#D8C29B]/50">
          <div className="p-5 bg-[#F3ECE2] rounded-2xl border border-[#D8C29B]/60 shadow-xs space-y-1.5">
            <div className="flex items-center space-x-2 text-[#8C6239]">
              <Sparkles className="w-4 h-4" />
              <p className="text-xs font-serif font-bold tracking-wide uppercase">100% Hand Handcrafted</p>
            </div>
            <p className="text-xs text-[#6A5948] leading-relaxed">
              Zero mass production. Poured individually in small batches in our sunlit atelier.
            </p>
          </div>

          <div className="p-5 bg-[#F3ECE2] rounded-2xl border border-[#D8C29B]/60 shadow-xs space-y-1.5">
            <div className="flex items-center space-x-2 text-[#8C6239]">
              <Award className="w-4 h-4" />
              <p className="text-xs font-serif font-bold tracking-wide uppercase">Clean Botanical Alchemy</p>
            </div>
            <p className="text-xs text-[#6A5948] leading-relaxed">
              Pure soy & coconut wax, lead-free organic wicks, and phthalate-free fragrance oils.
            </p>
          </div>
        </div>

        {/* Signature */}
        <div className="pt-2 text-right">
          <p className="font-serif italic text-[#8C6239] text-xl sm:text-2xl">Warmly, Rashmi</p>
          <p className="text-[11px] text-[#B5895A] font-sans uppercase tracking-[0.2em] font-semibold mt-0.5">
            Master Chandler & Founder
          </p>
        </div>
      </div>
    </LuxuryModal>
  );
};
